<script setup lang="ts">
import { useBiometricAuth } from '~/composables/useBiometricAuth';
import { useBiometricColdStartGate } from '~/composables/useBiometricColdStartGate';

/**
 * Gate de biometria no cold start, estilo "Betano".
 *
 * Fluxo:
 *  1. Marca `isGateInProgress=true` ANTES de qualquer coisa — o
 *     ApiService usa esse flag para suprimir toasts/redirects de 401
 *     enquanto o prompt biometrico esta na tela.
 *  2. Renderiza overlay transparente fullscreen para impedir cliques
 *     na home enquanto o prompt nativo do SO esta aberto.
 *  3. Se ja existir sessao ativa (cookie zumbi), invalida proativamente
 *     via logout({ origin: 'system' }) ANTES do prompt biometrico —
 *     garante estado limpo no re-login e elimina respostas de
 *     "token has been blacklisted" do backend JWT.
 *  4. Dispara `unlock()`.
 *  5. OK → re-submete /auth/login via store (sempre, ja em estado limpo).
 *  6. Cancel/falha → logout silencioso (origin='system'), credencial
 *     preservada (nao marca biometric_locked) para retry pelo botao
 *     do Login.vue.
 *
 * Sem dismiss-on-click: o overlay captura cliques para que o usuario
 * nao consiga interagir com a home enquanto o prompt esta na tela.
 */

const {
  isAvailableEnvironment,
  checkAvailability,
  unlock,
  isLockedFlagSet,
  isDisabledFlagSet,
} = useBiometricAuth();

const {
  shouldFire,
  markFired,
  unmarkFired,
  beginGate,
  endGate,
  isGateInProgress,
} = useBiometricColdStartGate();

const { $metrics } = useNuxtApp();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { login: storeLogin, logout: storeLogout } = useAuthStore();
const { isAppSource } = useLayout();
const { openToaster } = useLayoutStore();

const overlayVisible = ref(false);
const isLogging = ref(false);

/**
 * Confirma que o backend esta REALMENTE alcancavel antes de pedir biometria.
 * Hibrido em duas camadas:
 *
 * 1. `navigator.onLine === false` → offline; aborta na hora, sem bater na rede.
 *    Mesma convencao usada no resto do front (MaisPagouGrid.vue /
 *    ApostatudoGames.vue).
 * 2. Online segundo o device NAO garante que o login completaria (internet
 *    lenta, wifi sem internet, backend fora). Entao faz um probe real:
 *    `/api/*` e same-origin e fica fora do cache do service worker
 *    (navigateFallbackDenylist), entao este fetch testa a rede de verdade —
 *    nao o cache. Qualquer resposta HTTP (incl. 4xx/5xx, ex.: 405 no HEAD)
 *    significa "backend alcancavel"; so erro de rede ou timeout = inalcancavel.
 *
 * Sem isso, o `POST /auth/login` nao completaria e o prompt biometrico viraria
 * um beco sem saida.
 *
 * Usa AbortController + setTimeout (AbortSignal.timeout nao existe na WebView
 * alvo ~Chrome 87).
 */
async function isBackendReachable(timeoutMs = 4000): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return false;
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    await fetch('/api/auth/login', {
      method: 'HEAD',
      cache: 'no-store',
      signal: controller.signal,
    });

    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

async function runGate() {
  if (!shouldFire()) {
    return;
  }

  if (!isAvailableEnvironment()) {
    return;
  }

  markFired();
  beginGate();

  try {
    const availability = await checkAvailability();

    if (!availability.hasHardware) {
      return;
    }

    if (!availability.isEnrolled) {
      return;
    }

    if (!availability.hasCredential) {
      return;
    }

    if (isLockedFlagSet()) {
      return;
    }

    if (isDisabledFlagSet()) {
      return;
    }

    // Vai pedir biometria — bloqueia interacao com a home a partir
    // daqui (antes do logout preemptivo, que pode demorar).
    overlayVisible.value = true;

    // Sessao zumbi: cookie ainda existe no WebView mas o backend pode ter
    // o JWT blacklisted. Invalida proativamente antes do prompt, para que
    // o re-login pos-biometria parta de um estado limpo. Logout silencioso
    // (origin=system) NAO marca biometric_locked.
    if (isAuthenticated.value) {
      try {
        await storeLogout({
          displayMessage: false,
          redirectToHome: false,
          origin: 'system',
        });
      } catch {}
    }

    // A sessao ja foi travada acima (logout preemptivo) — o usuario esta
    // deslogado a partir daqui, garantindo que nunca ha acesso sem biometria.
    // So pedimos a biometria se o backend responde: sem isso, o re-login pos
    // biometria nao completaria e o prompt seria inutil. Offline o app ja
    // exibe a tela "sem conexao".
    const reachable = await isBackendReachable();

    if (!reachable) {
      // Falta de conexao NAO e decisao final do gate — e "tenta de novo quando
      // voltar". Re-arma para que o listener `online` (e qualquer reload)
      // possa rodar o gate de novo. O usuario continua deslogado nesse meio
      // tempo, entao o invariante "sempre exige biometria" segue valendo.
      unmarkFired();

      return;
    }

    const result = await unlock();

    if (!result.success || !result.login || !result.password) {
      if (result.error === 'biometric_changed') {
        openToaster({
          message:
            'Detectamos uma alteração na biometria do aparelho. Faça login e ative a biometria novamente.',
          type: 'warning',
        });
      }

      // Ja fizemos logout preemptivo acima, mas mantemos esta chamada
      // como garantia (idempotente quando ja deslogado).
      try {
        await storeLogout({
          displayMessage: false,
          redirectToHome: false,
          origin: 'system',
        });
      } catch {}

      return;
    }

    isLogging.value = true;

    try {
      const response = await storeLogin({
        login: result.login,
        email: result.login,
        password: result.password,
        app_source: isAppSource.value ? 'app' : 'web',
        captcha_token: '',
      });

      $metrics.push('appsflyer')?.handle('af_login', {
        af_login_method: 'biometric',
        _customer_user_id: response?.data?.user?.id,
      });
    } catch {
      try {
        await storeLogout({
          displayMessage: false,
          redirectToHome: false,
          origin: 'system',
        });
      } catch {}
    }
  } finally {
    overlayVisible.value = false;
    isLogging.value = false;
    endGate();
  }
}

// Listener seguro: bloqueia eventos de mouse/touch enquanto o overlay
// esta visivel. Pure capture, sem dismiss-on-click.
function swallow(e: Event): void {
  e.stopPropagation();
  e.preventDefault();
}

// Re-arma na volta da conexao (mesma convencao de MaisPagouGrid.vue). Cobre o
// caso em que o gate abortou por falta de backend: quando a rede volta, roda
// de novo e pede a biometria. Guard `isGateInProgress` evita disparo duplo se
// o evento chegar com um gate ja em andamento.
function handleOnline(): void {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return;
  }
  if (isGateInProgress.value) {
    return;
  }
  runGate();
}

onMounted(() => {
  runGate();

  if (typeof window !== 'undefined') {
    window.addEventListener('online', handleOnline);
  }
});

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('online', handleOnline);
  }
});
</script>

<template>
  <Teleport
    v-if="overlayVisible"
    to="body"
  >
    <div
      aria-hidden="true"
      class="biometric-gate-overlay"
      :class="{ 'biometric-gate-overlay--loading': isLogging }"
      @click.capture="swallow"
      @pointerdown.capture="swallow"
      @touchend.capture="swallow"
      @touchstart.capture="swallow"
    >
      <BaseLoadingIconLoader
        v-if="isLogging"
        duration="500ms"
        :show="true"
        size="40px"
      />
    </div>
  </Teleport>
</template>

<style scoped>
.biometric-gate-overlay {
  position: fixed;
  inset: 0;
  z-index: 2147483647;
  background: transparent;
  touch-action: none;
  -webkit-tap-highlight-color: transparent;
}

.biometric-gate-overlay--loading {
  background: #030a11;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
